import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addRiskLogEntry,
  createRisk,
  fetchRiskAttachments,
  fetchRiskLog,
  fetchRisks,
  removeRiskAttachment,
  setRiskActive,
  transitionRisk,
  updateRisk,
  uploadRiskAttachment,
  type CreateRiskInput,
  type UpdateRiskInput,
} from '../api/risk.api';

export const RISK_KEYS = {
  list: (projectId: string) => ['risks', projectId] as const,
  log: (projectId: string, riskId: string) => ['risks', projectId, riskId, 'log'] as const,
  attachments: (projectId: string, riskId: string) => ['risks', projectId, riskId, 'attachments'] as const,
};

export function useRisks(projectId: string) {
  return useQuery({ queryKey: RISK_KEYS.list(projectId), queryFn: () => fetchRisks(projectId), enabled: Boolean(projectId) });
}

export function useCreateRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRiskInput) => createRisk(projectId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.list(projectId) }),
  });
}

export function useUpdateRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, data }: { riskId: string; data: UpdateRiskInput }) => updateRisk(projectId, riskId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.list(projectId) }),
  });
}

export function useTransitionRisk(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, toStep }: { riskId: string; toStep: string }) => transitionRisk(projectId, riskId, toStep),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.list(projectId) }),
  });
}

// Unbound counterpart to useTransitionRisk(projectId) above — projectId travels in the mutate
// payload instead of being fixed at hook-creation, exactly like useTransitionIssue() in
// useIssues.ts. A cross-project list (Home's Risks tab) holds rows from many projects, so it
// can't bind a single projectId when it calls the hook. The bound version stays for
// project-scoped callers like ProjectRisksTab.
export function useTransitionRiskAcrossProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, riskId, toStep }: { projectId: string; riskId: string; toStep: string }) =>
      transitionRisk(projectId, riskId, toStep),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: RISK_KEYS.list(projectId) }),
  });
}

export function useSetRiskActive(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, active }: { riskId: string; active: boolean }) => setRiskActive(projectId, riskId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.list(projectId) }),
  });
}

export function useRiskLog(projectId: string, riskId: string) {
  return useQuery({
    queryKey: RISK_KEYS.log(projectId, riskId),
    queryFn: () => fetchRiskLog(projectId, riskId),
    enabled: Boolean(riskId),
  });
}

export function useAddRiskLogEntry(projectId: string, riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => addRiskLogEntry(projectId, riskId, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.log(projectId, riskId) }),
  });
}

export function useRiskAttachments(projectId: string, riskId: string) {
  return useQuery({
    queryKey: RISK_KEYS.attachments(projectId, riskId),
    queryFn: () => fetchRiskAttachments(projectId, riskId),
    enabled: Boolean(riskId),
  });
}

export function useUploadRiskAttachment(projectId: string, riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadRiskAttachment(projectId, riskId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.attachments(projectId, riskId) }),
  });
}

export function useRemoveRiskAttachment(projectId: string, riskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removeRiskAttachment(projectId, riskId, attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RISK_KEYS.attachments(projectId, riskId) }),
  });
}
